<template>
    <div>
        <Panel
            :title="$t('WhatsApp Accounts')"
            :page="page"
            :delete-button="selected.length > 0"
            :add-button="canEdit ? $t('Add Account') : ''"
            @input="addAccount"
            @delete="handleDelete"
            @change="setPage"
        >
            <table id="whatsappaccounts_table" class="cic-table">
                <thead>
                    <tr>
                        <th>{{ $t('Account Name') }}</th>
                        <th>{{ $t('Phone Number') }}</th>
                        <th>{{ $t('Channel ID') }}</th>
                        <th v-if="canEdit" class="td-checkbox"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="account in accounts">
                        <td>
                            <el-tooltip
                                v-if="!account.havePermissions"
                                :content="$t('Please grant permissions for this account')"
                                placement="left"
                                effect="light"
                                class="missing-permissions-warning-tooltip"
                            >
                                <WarningFilled id="missing-permissions-warning" />
                            </el-tooltip>
                            {{ account.accountName }}
                        </td>
                        <td>{{ account.phoneNumber }}</td>
                        <td>{{ account.channelId }}</td>
                        <td v-if="canEdit">
                            <cic-checkbox
                                :value="selected.indexOf(account.accountId) !== -1"
                                @click="handleSelected(account.accountId)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </Panel>

        <EditWhatsAppAccount
            v-if="item !== null && !$store.showLoadingOverlay"
            :item="item"
            @blur="closePopup"
            @input="saveItem"
            @change="updateFieldValue"
        />
    </div>
</template>

<script>
    import Panel from '@/components/container/Panel.vue';
    import TableUtils from '@/mixins/TableUtils';
    import '@/css/socialmedia.css';
    import { deleteItems, load, post, save } from '@/utils/Http';
    import CicCheckbox from '@/components/inputs/CicCheckbox.vue';
    import EditWhatsAppAccount from '@/feature/social/whatsAppAccounts/EditWhatsAppAccount.vue';
    import { showErrorMessage, showSuccessMessage } from '@/utils/Notification';
    import { ElTooltip } from 'element-plus';
    import { WarningFilled } from '@element-plus/icons-vue';

    export default {
        name: 'WhatsAppAccounts',
        components: {
            EditWhatsAppAccount,
            CicCheckbox,
            Panel,
            WarningFilled,
            'el-tooltip': ElTooltip,
        },
        mixins: [TableUtils],
        props: {
            authorise: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                items: [],
                item: null,
                selected: [],
                page: 1,
                loadingOverlay: null,
            };
        },
        computed: {
            accounts() {
                return this.items.slice((this.page - 1) * 20, this.page * 20);
            },
            canEdit() {
                return this.$store.hasRight('EDIT_ACCESS_TYPE_24');
            },
        },
        created() {
            if (this.authorise) {
                this.handleAuthorise();
                return;
            }

            this.loadTable();
        },
        methods: {
            handleAuthorise() {
                this.loadingOverlay = this.$loading({
                    text: this.$t('Waiting for authorisation...'),
                });

                load('whatsapp/get', {}, (response) => {
                    this.items = response.accounts;

                    const queryParameters = new URLSearchParams(window.location.search);
                    const clientId = queryParameters.get('client');
                    const channelIds = queryParameters.get('channels')?.slice(1, -1)?.split(',') || [];
                    const revokedChannelIds = queryParameters.get('revoked')?.slice(1, -1)?.split(',') || [];

                    const existingChannelIds = this.items.map((item) => item.channelId);
                    const newChannelIds = channelIds.filter((channelId) => !existingChannelIds.includes(channelId));

                    if (newChannelIds.length === 0 && revokedChannelIds.length === 0) {
                        this.loadingOverlay.close();
                        showErrorMessage(this.$t('No new channel was added'));
                        return;
                    } else if (newChannelIds.length === 0 && revokedChannelIds.length > 0) {
                        this.loadingOverlay.close();
                        showSuccessMessage(this.$t('Channel permissions revoked'));
                        return;
                    }

                    post(
                        'whatsapp/getnumberbychannel',
                        { clientId: clientId, channelId: newChannelIds[0] },
                        (response) => {
                            this.loadingOverlay.close();
                            this.item = {
                                accountId: null,
                                phoneNumber: response.phoneNumber,
                                clientId: clientId,
                                channelId: newChannelIds[0],
                                accountName: '',
                                timeoutMessage: '',
                            };
                        },
                    ).then((response) => {
                        if (response && response.status === 'error') {
                            this.loadingOverlay.close();
                            showErrorMessage(this.$t(response.errorMessage));
                        }
                    });
                });
            },
            loadTable() {
                this.page = 1;
                this.selected = [];
                this.closePopup();
                load('whatsapp/get', {}, (response) => {
                    this.items = response.accounts;
                });
            },
            handleDelete() {
                deleteItems('whatsapp/delete', { accountIds: this.selected }, this.loadTable);
            },
            addAccount() {
                this.loadingOverlay = this.$loading({
                    text: this.$t('Redirecting to 360Dialog...'),
                });

                post('whatsapp/getredirecturl', {}, (response) => {
                    window.location.assign(response.hubRedirectUrl);
                }).then((response) => {
                    if (response.status === 'error') {
                        this.loadingOverlay.close();
                        showErrorMessage(this.$t(response.errorMessage));
                    }
                });
            },
            saveItem() {
                save('whatsapp/add', { ...this.item }, this.loadTable);
            },
        },
    };
</script>
